// ============================================================
// LOGIN (P2) — 公開ミニ紹介ページ + ログインカード。
// Google OAuth 審査対応（2026-07）: 未ログインでも「アプリの目的・機能・
// 運営者・データの扱い」が読めるランディング構成に格上げした。
// - ヒーロー: アプリ名 + 目的の一文
// - 機能カード x4（議事録AI要約 / カレンダー / Tasks同期 / ナレッジ）
// - Google 連携のデータ利用範囲を1行明記（本人データのみ・組織外非共有）
// - フッター: 運営者 TEMP LLC / 問い合わせ / プライバシーポリシー
// ログインカード（Google でログイン）は lg で右カラム、モバイルでは
// ヒーロー直下に表示。ログイン成功後の viewer 確定は AuthContext が行う。
//
// `unregisteredEmail` がある場合（Google ログインは成功したが members に
// 未登録）は、断絶させず「管理者に連絡」の申請導線を示す（PRD §11 UX）。
// ============================================================
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Icon } from '../components/Icons';

// ── 公開紹介セクションの機能カード（ログイン不要で読める） ──
const FEATURES: { icon: string; title: string; body: string }[] = [
  {
    icon: 'sparkles',
    title: '議事録のAI要約とタスク抽出',
    body: 'Google Meet の議事録ドキュメントをご本人の操作で取り込み、AIが要約と担当者ごとのタスクを自動で抽出します。',
  },
  {
    icon: 'calendar-days',
    title: 'Google カレンダー連携',
    body: '自分の予定・会議の一覧をポータル上でまとめて確認できます。',
  },
  {
    icon: 'list-todo',
    title: 'タスク管理（Google Tasks 同期）',
    body: 'ポータルのタスクと Google Tasks を双方向で同期。どちらで更新しても同じ状態に保たれます。',
  },
  {
    icon: 'book-open',
    title: '社内ナレッジ共有',
    body: '議事録・決定事項・社内ドキュメントを横断検索して、必要な情報にすぐたどり着けます。',
  },
];

export default function Login() {
  const { signInWithGoogle, signOut, unregisteredEmail } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleLogin() {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch {
      // popup-closed / network 等。技術ログは出さず、メンバー向けに簡潔に。
      setError('ログインに失敗しました。もう一度お試しください。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-base text-charcoal flex flex-col">
      <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14 flex-1 flex flex-col">
        {/* ── brand header（公開） ── */}
        <header className="flex items-center gap-3 mb-8 sm:mb-10">
          <div className="grid place-items-center w-10 h-10 rounded-soft bg-charcoal text-white text-base font-bold shrink-0">
            T
          </div>
          <div>
            <p className="text-sm font-bold tracking-wide leading-tight">TEMP 社内ポータル</p>
            <p className="text-2xs text-slate-sub leading-tight">Team Portal — TEMP LLC</p>
          </div>
        </header>

        <main className="flex-1">
          {/* ── ヒーロー（公開・ログイン不要で読める） ── */}
          <section className="mb-8">
            <h1 className="text-xl sm:text-2xl font-bold leading-snug tracking-wide">
              会議の議事録・タスク・予定を、
              <br className="hidden sm:block" />
              1か所に集約する社内ポータル
            </h1>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed max-w-xl">
              TEMP 社内ポータルは、TEMP LLC のメンバー専用の業務ポータルです。会議の議事録・タスク・予定・社内ナレッジを1か所に集約し、Google
              カレンダー・Google Tasks・Google ドキュメント（Meet 議事録）と連携して日々の業務を効率化します。
            </p>
          </section>

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px] items-start">
          {/* ── 機能紹介（公開） ── */}
          <section className="min-w-0 order-2 lg:order-1">
            {/* feature cards */}
            <div className="grid gap-3 sm:grid-cols-2">
              {FEATURES.map((f) => (
                <div key={f.title} className="rounded-card bg-white border border-hair p-4">
                  <div className="flex items-center gap-2.5 mb-2">
                    <span className="grid place-items-center w-8 h-8 rounded-soft bg-sky-soft text-sky-deep shrink-0">
                      <Icon name={f.icon} size={16} />
                    </span>
                    <h2 className="text-sm font-semibold leading-snug">{f.title}</h2>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>

            {/* data-usage note（審査対応 + 事実） */}
            <div className="mt-4 flex items-start gap-2.5 rounded-card bg-white border border-hair px-4 py-3">
              <Icon name="shield-check" size={16} className="text-success-deep shrink-0 mt-0.5" />
              <p className="text-xs text-slate-600 leading-relaxed">
                Google 連携では、ログインしたご本人のカレンダー・議事録ドキュメントを読み取り専用で、タスクは同期のため読み書きで利用します。取得したデータを組織外に共有することはありません。
              </p>
            </div>
          </section>

          {/* ── ログインカード（メンバー専用） ── */}
          <aside className="order-1 lg:order-2 w-full max-w-sm mx-auto lg:mx-0 lg:sticky lg:top-10">
            <div className="rounded-card bg-white border border-hair shadow-sm p-7">
              <div className="text-center mb-5">
                <h2 className="text-base font-bold tracking-wide">メンバーログイン</h2>
                <p className="text-2xs text-slate-sub mt-1">
                  TEMP LLC のメンバー専用（招待されたメンバーのみ利用できます）
                </p>
              </div>

              {unregisteredEmail ? (
                // ── 未登録ガード（ログインはできたが members に居ない）──
                <div className="space-y-4">
                  <div className="rounded-soft bg-amber-soft border border-amber-brand/30 px-4 py-3 text-sm text-amber-deep">
                    <p className="font-semibold mb-1">アクセス権がありません</p>
                    <p className="text-xs leading-relaxed">
                      <span className="font-medium break-all">{unregisteredEmail}</span>{' '}
                      はまだポータルに登録されていません。管理者（たいき）に登録を依頼してください。
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void signOut()}
                    className="w-full rounded-soft border border-hair bg-white px-4 py-2.5 text-sm font-medium text-charcoal hover:bg-base"
                  >
                    別のアカウントでログイン
                  </button>
                </div>
              ) : (
                // ── 通常のログイン ──
                <div className="space-y-4">
                  <p className="text-sm text-slate-600 text-center leading-relaxed">
                    会社の Google アカウントでログインすると、あなたの権限内の情報がまとめて見られます。
                  </p>
                  <button
                    type="button"
                    onClick={() => void handleLogin()}
                    disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2.5 rounded-soft bg-charcoal px-4 py-2.5 text-sm font-semibold text-white hover:bg-charcoal-700 disabled:opacity-60"
                  >
                    {/* Google G mark */}
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        fill="#FFC107"
                        d="M43.6 20.5h-1.9V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"
                      />
                      <path
                        fill="#FF3D00"
                        d="m6.3 14.7 6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
                      />
                      <path
                        fill="#4CAF50"
                        d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.3C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"
                      />
                      <path
                        fill="#1976D2"
                        d="M43.6 20.5h-1.9V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.3C41.1 36.3 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z"
                      />
                    </svg>
                    {busy ? 'ログイン中…' : 'Google でログイン'}
                  </button>
                  {error && <p className="text-xs text-danger-deep text-center">{error}</p>}
                </div>
              )}
            </div>
          </aside>
          </div>
        </main>

        {/* ── footer（公開・運営者 / 問い合わせ / プライバシーポリシー） ── */}
        <footer className="mt-10 pt-5 border-t border-hair">
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-2xs text-slate-sub">
            <span>運営者: TEMP LLC</span>
            <a
              href="mailto:info@biz-tsunagu.com"
              className="inline-flex items-center gap-1.5 hover:underline hover:text-charcoal"
            >
              <Icon name="mail" size={12} />
              info@biz-tsunagu.com
            </a>
            <Link to="/privacy" className="hover:underline hover:text-charcoal">
              プライバシーポリシー
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
